import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { displayName } from '../../utils/userLabel';
import { REACTION_EMOJI, REACTIONS, editable, shortTime, systemText } from '../../utils/chat/messages';
import Dialog from '../ui/Dialog';
import * as S from '../specimens/styles';
import CollectionCard from '../events/CollectionCard';
import MessageAttachments from './MessageAttachments';

/*
 * One message: who and when, the text (or that it was removed), its
 * reactions, and what this person may do with it -- react; on their own text,
 * edit within 15 minutes or take it back; on someone else's, report it. A
 * system line ("Ada added Bo") is a centred note. A `media` message shows
 * what it carries -- photographs, voice notes, places, records
 * (MessageAttachments) -- and its caption under them. `onChanged(message)`
 * hands back what the server answered.
 */
export default function MessageItem({ message, me, canSend, onChanged }) {
  const { t, i18n } = useTranslation(['chat', 'common']);
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmBack, setConfirmBack] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [error, setError] = useState('');
  const mine = message.sender_id === me;

  if (message.kind === 'system') {
    return (
      <li className="chat-system" data-testid="chat-system" style={{ textAlign: 'center', ...S.hint, margin: '8px 0' }}>
        {systemText(message, t)} · {shortTime(message.created_at, i18n.language)}
      </li>
    );
  }

  const act = (call) => {
    setError('');
    return call().then((r) => onChanged?.(r.data.message || r.data)).catch((e) => setError(errorText(e)));
  };
  const react = (reaction, on) => {
    setPicking(false);
    setError('');
    api.post(`/chat/messages/${message.message_id}/reactions`, { reaction, on })
      .then((r) => onChanged?.({ ...message, reactions: r.data.reactions }))
      .catch((e) => setError(errorText(e)));
  };

  return (
    <li data-testid="chat-message" data-mine={mine ? 'true' : 'false'} data-removed={message.removed ? 'true' : 'false'}
      style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
      <div style={{ ...S.hint, marginBottom: 2 }}>
        {!mine && <strong style={{ color: 'var(--c-text-fg)' }}>{displayName(message.sender_name, t) || t('someone')} </strong>}
        {shortTime(message.created_at, i18n.language)}
        {message.edited_at && !message.removed && <> · {t('edited')}</>}
      </div>
      <div style={{ ...bubble, ...(mine ? mineBubble : {}), ...(message.removed ? removedBubble : {}) }}>
        {message.removed ? (
          <em>{t(`removed.${message.removal_reason || 'retracted'}`)}</em>
        ) : message.kind === 'card' ? (
          // A live event's Collection Card (routes/event_cards.py): the card's own fields.
          <CollectionCard card={message.card} compact />
        ) : message.kind === 'media' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <MessageAttachments attachments={message.attachments || []} me={me} />
            {message.body && (
              <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} dir="auto">{message.body}</span>
            )}
          </div>
        ) : editing !== null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
            <textarea value={editing} onChange={(e) => setEditing(e.target.value)} rows={2} maxLength={4000}
              style={S.input} data-testid="chat-edit-text" aria-label={t('editMessage')} />
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button type="button" style={small} onClick={() => setEditing(null)}>{t('cancel')}</button>
              <button type="button" style={small} disabled={!editing.trim()} data-testid="chat-edit-save"
                onClick={() => act(() => api.put(`/chat/messages/${message.message_id}`, { body: editing }))
                  .then(() => setEditing(null))}>{t('save')}</button>
            </div>
          </div>
        ) : (
          <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} dir="auto">{message.body}</span>
        )}
      </div>
      {message.reactions?.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
          {message.reactions.map((r) => (
            <button key={r.reaction} type="button" disabled={!canSend} onClick={() => react(r.reaction, !r.mine)}
              aria-pressed={r.mine} data-testid={`chat-reaction-${r.reaction}`}
              title={t(r.mine ? 'tip.unreact' : 'tip.react', { reaction: t(`reaction.${r.reaction}`) })}
              style={{ ...chip, ...(r.mine ? chipOn : {}) }}>
              <span aria-hidden="true">{REACTION_EMOJI[r.reaction]}</span> {r.count}
            </button>
          ))}
        </div>
      )}
      {!message.removed && editing === null && (
        <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
          {canSend && (
            <button type="button" style={link} onClick={() => setPicking((p) => !p)} aria-expanded={picking}
              data-testid="chat-react">{t('react')}</button>
          )}
          {canSend && editable(message, me) && (
            <button type="button" style={link} onClick={() => setEditing(message.body)} data-testid="chat-edit">
              {t('edit')}
            </button>
          )}
          {canSend && mine && (
            <button type="button" style={link} onClick={() => setConfirmBack(true)} data-testid="chat-retract">
              {t('takeBack')}
            </button>
          )}
          {!mine && (
            <button type="button" style={link} onClick={() => setReporting(true)} data-testid="chat-report">
              {t('report')}
            </button>
          )}
        </div>
      )}
      {picking && (
        <div role="group" aria-label={t('react')} style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          {REACTIONS.map((r) => (
            <button key={r} type="button" style={chip} onClick={() => react(r, true)}
              aria-label={t(`reaction.${r}`)} data-testid={`chat-pick-${r}`}>{REACTION_EMOJI[r]}</button>
          ))}
        </div>
      )}
      {confirmBack && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, ...S.hint }}>
          {t('takeBackConfirm')}
          <button type="button" style={small} onClick={() => setConfirmBack(false)}>{t('cancel')}</button>
          <button type="button" style={{ ...small, color: 'var(--c-danger-fg)' }} data-testid="chat-retract-confirm"
            onClick={() => act(() => api.post(`/chat/messages/${message.message_id}/retract`))
              .then(() => setConfirmBack(false))}>{t('takeBack')}</button>
        </div>
      )}
      {error && <p role="alert" style={S.error}>{error}</p>}
      {reporting && <ReportDialog message={message} onClose={() => setReporting(false)} />}
    </li>
  );
}

function ReportDialog({ message, onClose }) {
  const { t } = useTranslation(['chat', 'common']);
  const [reason, setReason] = useState('');
  const [state, setState] = useState('');
  const send = () => {
    setState('sending');
    api.post(`/chat/messages/${message.message_id}/report`, { reason: reason.trim() })
      .then(() => setState('sent'))
      .catch((e) => setState(errorText(e)));
  };
  return (
    <Dialog title={t('reportTitle')} onClose={onClose} testId="chat-report-dialog" width={460}>
      {state === 'sent' ? (
        <p role="status">{t('reportSent')}</p>
      ) : (
        <>
          <p style={S.hint}>{t('reportHint')}</p>
          <label style={S.label}>
            {t('reportReason')}
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={1000}
              style={S.input} data-testid="chat-report-reason" />
          </label>
          {state && state !== 'sending' && <p role="alert" style={S.error}>{state}</p>}
        </>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button type="button" style={S.ghostBtn} onClick={onClose}>{t('close')}</button>
        {state !== 'sent' && (
          <button type="button" style={S.btn()} disabled={state === 'sending' || reason.trim().length < 3}
            onClick={send} data-testid="chat-report-send">{t('reportSend')}</button>
        )}
      </div>
    </Dialog>
  );
}

const bubble = {
  background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 12, padding: '8px 12px',
  maxWidth: 'min(560px, 85%)', color: 'var(--c-text-fg)',
};
const mineBubble = { background: 'var(--c-primary-soft)', borderColor: 'transparent' };
const removedBubble = { color: 'var(--c-text-muted-fg)', background: 'transparent' };
const chip = {
  border: '1px solid var(--c-border)', background: 'var(--c-surface)', borderRadius: 999, padding: '0 8px',
  fontSize: 13, cursor: 'pointer', color: 'var(--c-text-fg)', minHeight: 26,
};
const chipOn = { borderColor: 'var(--c-primary)', background: 'var(--c-primary-soft)' };
const link = {
  background: 'none', border: 'none', padding: '0 4px', fontSize: 12, cursor: 'pointer',
  color: 'var(--c-text-muted-fg)', textDecoration: 'underline',
};
const small = { ...S.ghostBtn, padding: '2px 8px', fontSize: 12 };
